import React, { memo, useMemo, useRef } from 'react';
import { IImage } from '../../modules/reducers';
import {
  CollectionDnDModule,
  getCollectionImageURL,
  getTranslationKey,
  isMobile
} from '../../modules/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeModalState,
  selectImageForSlideshow,
  selectImages,
  setGifMetaData,
  setImageTitle,
  setIsImageDarggingAction,
  setSmallScreenImageTitle,
  showNotifyFail,
  deleteImage
} from '../../modules/actions';
import { get } from 'lodash';
import { FaRegTrashAlt, FaStar } from 'react-icons/fa';
import FaUsersViewfinder from '../../assets/img/icons/users-viewfinder-solid.svg?react';
import { ActionButtonsTypes, ImageCardActions } from './ImageCardActions';
import { IFavorite } from '../../modules/types/selections';
import { ImageCardFavoritesIcons } from './ImageCardFavoritesIcons';
import theme from '../../assets/css/theme';
import { LazyLoadImage, LazyLoadImageProps } from 'react-lazy-load-image-component';
import { UncontrolledTooltip } from 'reactstrap';
import {
  getCollectionsNew,
  getImageByID,
  getSelectedImagesIDs,
  isRenderableImage
} from '../../modules/selectors';
import { useMultipleSlideSelection } from '../../modules/hooks';

import styled from 'styled-components';

const getMultiSelectMargin = (count?: number) => (count && count > 1 ? `-${count / 2}%` : 0);
const Wrapper = styled.div<{ selectedCount?: number; noDrag?: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  transform: translate3d(0, 0, 0);
  user-select: none;
  width: 100%;
  height: 150px;
  padding: ${({ noDrag }) => (noDrag ? '0' : '10px 5px 0 5px')};

  .multi-selection {
    margin-left: ${(props) => getMultiSelectMargin(props.selectedCount)};

    &:first-child {
      margin: 0;
    }
  }

  &:hover {
    .image-tools {
      display: block;
    }
  }

  &.hide-image {
    opacity: 0;
  }

  &.drop-start-image {
    &:after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba(255, 243, 205, 0.7);
    }

    .image-tools {
      display: none;
    }
  }
`;

const selectedBorder = `2px solid ${theme.commonColors.second}`;
const ImageInner = styled.div<{
  isDraggable?: boolean;
  isSelected?: boolean;
  withOverlay?: boolean;
}>`
  border: ${(props) => (props?.isSelected ? selectedBorder : ' 1px solid #c8ced3')};
  cursor: ${(props) => props?.isDraggable && 'move'};
  position: relative;
  transition: left 0.3s linear;
  left: 0;
  width: 100%;
  border-radius: 5px;
  background: #ededed;
`;

const StarImage = styled(FaStar)`
  position: absolute;
  right: 10px;
  top: 5px;
`;
const DeleteTitleIcon = styled(FaRegTrashAlt)`
  position: absolute;
  left: 10px;
  top: 5px;
  cursor: pointer;
  z-index: 2;
`;
const SetFocalPointIcon = styled(FaUsersViewfinder)`
  position: absolute;
  width: 20px;
  height: 20px;
  left: 10px;
  bottom: 5px;
  cursor: pointer;
  z-index: 2;
`;

interface ImageProps extends LazyLoadImageProps {
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const Image = styled<React.FC<ImageProps>>(LazyLoadImage)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const CompressText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  padding: 0 8px;
  color: #7b7b7b;
  font-size: 12px;
  font-weight: 600;
  z-index: 2;
  text-align: center;
`;

const CompressFileName = styled.div`
  margin-bottom: 4px;
  color: #5c5c5c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompressDeleteButton = styled.button`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${theme.buttonsColors.danger};
  cursor: pointer;
  z-index: 3;
  font-size: 16px;
  padding: 4px;

  &:hover {
    opacity: 0.7;
  }
`;

interface ImageRendererProps {
  image: IImage;
  imageSource: string;
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onError?: () => void;
}

const noop = () => {};

const ImageRenderer: React.FC<ImageRendererProps> = ({
  imageSource,
  onLoad = noop,
  onError = noop
}) => {
  const [retryKey, setRetryKey] = React.useState(0);
  const [hadError, setHadError] = React.useState(false);

  React.useEffect(() => {
    setHadError(false);
  }, [imageSource]);

  React.useEffect(() => {
    if (!hadError) return;

    const handleOnline = () => {
      setRetryKey((k) => k + 1);
      setHadError(false);
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [hadError]);

  const handleError = () => {
    onError();

    // Retry only when the browser is offline; otherwise we might get stuck waiting for `online`.
    if (window.navigator.onLine === false) {
      setHadError(true);
    }
  };

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setHadError(false);
    onLoad(event);
  };

  return (
    <Image
      key={`${imageSource}-${retryKey}`}
      src={imageSource}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
};

const Cover = styled.div<{ isSelected: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: transparent;
`;

const removePlaceHolder = (className: string) => {
  const dndPlaceholders = document.getElementsByClassName(className);
  while (dndPlaceholders.length > 0) {
    dndPlaceholders[0].remove();
  }
};

const appendMultiSelectContainer = (
  ev: React.DragEvent,
  isSelected: boolean,
  draggedItem: any,
  selectedImagesIDs: string[]
) => {
  const multi = document.getElementById('multi');

  const target = ev.target as HTMLDivElement;
  if (isSelected && multi) {
    const containerLength = selectedImagesIDs.length * parseInt(draggedItem.style.width);
    multi.style.width = containerLength + 'px';
    target.appendChild(multi);
  }
};

const hideMultiSelectContainerBack = () => {
  const multi = document.getElementById('multi');
  const initialMultiPlace = document.getElementById('multi-wrapper');
  if (multi && initialMultiPlace) {
    initialMultiPlace.appendChild(multi);
  }
};
const selectedImageClassName = 'selected-image';

const toggleSelectedImagesInDrag = (display: string) => {
  const selectedImages = document.getElementsByClassName(selectedImageClassName);
  Array.from(selectedImages).forEach((item: any) => {
    item.style.display = display;
  });
};
const placeholderClassName = 'dnd-placeholder';
const imageInDragBodyClassName = 'image-drag-enabled';

interface IProps {
  image: IImage;
  noToolbar?: boolean;
  isUploading?: boolean;
  noDrag?: boolean;
  index?: number;
  isTitleImage?: boolean;
  isSmallScreenTitleImage?: boolean;
  favorite?: IFavorite;
  commentCount?: number;
  onCommentClick?: (imageId: string) => void;
  hideActionsButtons?: ActionButtonsTypes[];
  actionID?: string;
  className?: string;
  isInMultiSelection?: boolean;
  isSlideShowPage?: boolean;
  images?: IImage[];
  galleriesImages?: IImage[];
  style?: any;
  isStillCompressing?: boolean;
  isFailedUpload?: boolean;
}

export const ImageCard = memo((data: IProps) => {
  const dispatch = useDispatch();
  const dropContainerRef = useRef(null);
  const selectedImagesIDs = useSelector(getSelectedImagesIDs);
  const getImageById = useSelector(getImageByID);
  const { signedWilCardUrl } = useSelector(getCollectionsNew);
  const handleItemSelection = useMultipleSlideSelection();

  const dependents: any[] = [
    data.image?._id,
    data.image?.focalPoints,
    data.favorite,
    data.commentCount,
    data.onCommentClick,
    data.className,
    data.image?._watermark,
    data.image?.identifier,
    data.isUploading,
    data?.isInMultiSelection,
    data.isSlideShowPage,
    data?.noDrag,
    data?.images,
    data?.index,
    data?.style,
    data?.galleriesImages,
    data?.isStillCompressing,
    data?.isFailedUpload
  ];
  const {
    image,
    index,
    noToolbar,
    isTitleImage,
    isSmallScreenTitleImage,
    noDrag,
    favorite,
    commentCount,
    onCommentClick,
    hideActionsButtons,
    actionID,
    className = '',
    isInMultiSelection,
    isSlideShowPage,
    style,
    galleriesImages,
    isStillCompressing,
    isFailedUpload
  } = useMemo(() => data, dependents);

  if (!image) return null;

  const galleryImage = getImageById(image._id);
  const isSelected = !noToolbar && selectedImagesIDs?.includes(image._id);
  const isGalleryDnDEvent = (target: React.DragEvent | null) =>
    !noToolbar && target && ['image', 'multi'].includes(CollectionDnDModule.getType());

  const selectImageHandler = (e: React.MouseEvent<Element, MouseEvent>) => {
    if (noDrag) return;

    if (!noToolbar) {
      // failed-upload placeholders are not selectable — keep them out of
      // shift-range selections
      const selectImagesCallback = (finalList: IImage[]) =>
        dispatch(selectImages(finalList.filter(isRenderableImage).map(({ _id }) => _id)));

      const galleryFirstImageIndex = galleriesImages.findIndex(
        (i) => i.gallery_id === image.gallery_id
      );
      const currentIdx = index + galleriesImages.slice(0, galleryFirstImageIndex).length;

      handleItemSelection(e, currentIdx, galleriesImages, selectImagesCallback);
    }

    if (isSlideShowPage) {
      dispatch(
        selectImageForSlideshow({
          type: 'image',
          imageId: image._id,
          src: getCollectionImageURL(image, 'L', false, signedWilCardUrl)
        })
      );
    }
  };

  const onDropHandler = (ev: React.DragEvent, imageDropID?: string) => {
    if (noDrag) return;

    const drag_id = ev.dataTransfer.getData('drag_id');
    const drop_id = imageDropID || image._id;
    if (isGalleryDnDEvent(ev) && drag_id) {
      CollectionDnDModule.set({
        type: drag_id === 'multi' ? 'multi' : 'image',
        data: { drag_id, drop_id }
      });
    }
    hideMultiSelectContainerBack();
    toggleSelectedImagesInDrag('flex');

    document.body.classList.remove(imageInDragBodyClassName);
  };

  const onDragEnterHandler = () => {
    if (noDrag) return;

    const classes = get(dropContainerRef, 'current.classList', []);
    removePlaceHolder(placeholderClassName);
    const { drag_id } = CollectionDnDModule.getData();

    if (drag_id === image._id) return;
    if (isGalleryDnDEvent(dropContainerRef.current) && !classes.contains(placeholderClassName)) {
      const { top, left, width, height } = dropContainerRef.current?.style || {};

      const divEl = document.createElement('div');
      divEl.className = `${placeholderClassName}`;
      divEl.setAttribute(
        'style',
        `top: calc(${top} + 5px); left: calc(${left} - 1px); width: ${width}; height: ${height};`
      );

      divEl.ondrop = (ev: DragEvent) => onDropHandler(ev as unknown as React.DragEvent, image._id);

      const parent = dropContainerRef.current.parentElement;
      parent.insertBefore(divEl, dropContainerRef.current);
    }
  };

  const onDragStartHandler = (ev: React.DragEvent) => {
    if (noDrag) return;
    ev.stopPropagation();
    dispatch(setIsImageDarggingAction(ev.currentTarget.getBoundingClientRect()));
    document.body.classList.add(imageInDragBodyClassName);
    CollectionDnDModule.set({
      type: isSelected ? 'multi' : 'image',
      data: {
        drag_id: isSelected ? 'multi' : image._id,
        drop_id: ''
      }
    });
    ev.dataTransfer.setData('drag_id', isSelected ? 'multi' : image._id);
    ev.dataTransfer.effectAllowed = 'move';
    appendMultiSelectContainer(ev, isSelected, dropContainerRef.current, selectedImagesIDs);
    dropContainerRef.current.classList.add('drop-start-image');

    setTimeout(() => {
      dropContainerRef.current.classList.add('hide-image');
      if (isSelected) {
        toggleSelectedImagesInDrag('none');
      }
    });
  };

  const isDraggable = !noDrag && !isMobile();
  const selectedClass = isSelected ? selectedImageClassName : '';
  const isInMultiSelectionClass = isInMultiSelection ? 'multi-selection' : '';

  const removeTitleImage = () => dispatch(setImageTitle({ ...image, _id: '' }));
  const removeSmallScreenTitleImage = () =>
    dispatch(setSmallScreenImageTitle({ ...image, _id: '' }));

  // the same photo can be both the desktop and the small screen title image, so tooltip
  // ids must be scoped by screen type as well — reactstrap binds a tooltip to every
  // element matching the target id, and duplicates make several tooltips open at once
  const screenType = isTitleImage ? 'desktop' : 'mobile';
  const deleteTitleImageId = `title-image-delete-${screenType}-${image._id}`;
  const setFocalPointId = `title-image-set-focal-point-${screenType}-${image._id}`;

  const openSetFocalPointModal = () => {
    dispatch(
      changeModalState({
        key: 'setFocalPointModal',
        state: true,
        modalData: {
          imageId: image._id,
          image: galleryImage,
          defaultFocalPoint: image.focalPoints?.[screenType],
          screenType
        }
      })
    );
  };

  const imageSource = getCollectionImageURL(image, 'XS', false, signedWilCardUrl);

  const verifyImageDimensions = (imgDomObject: any, image: IImage): any => {
    if (image?.extension?.toLowerCase() === 'gif' && image?.width === 0 && image?.height === 0) {
      dispatch(
        setGifMetaData({
          image_id: image._id,
          dimensions: {
            width: imgDomObject.target.naturalWidth,
            height: imgDomObject.target.naturalHeight
          }
        })
      );
    }
  };

  const handleImageLoadError = () =>
    dispatch(showNotifyFail({ message: getTranslationKey('progressUploadModal.imageLoadError') }));

  const onDragEndHandler = () => {
    // Clean up the current dragged element
    if (dropContainerRef.current) {
      dropContainerRef.current.classList.remove('drop-start-image');
      dropContainerRef.current.classList.remove('hide-image');
    }

    // Also clean up any other elements that might have these classes (edge cases)
    document.querySelectorAll('.hide-image').forEach((el) => el.classList.remove('hide-image'));
    document
      .querySelectorAll('.drop-start-image')
      .forEach((el) => el.classList.remove('drop-start-image'));

    removePlaceHolder(placeholderClassName);
    hideMultiSelectContainerBack();
    toggleSelectedImagesInDrag('flex');
    document.body.classList.remove(imageInDragBodyClassName);
    dispatch(setIsImageDarggingAction(null));
  };

  return (
    <Wrapper
      id={image._id}
      style={style}
      noDrag={noDrag}
      data-cy="wrapper"
      ref={dropContainerRef}
      onDrop={onDropHandler}
      onDragOver={onDragEnterHandler}
      selectedCount={selectedImagesIDs.length}
      className={`${isInMultiSelectionClass} ${selectedClass}`}
    >
      {isStillCompressing || isFailedUpload ? (
        <ImageInner>
          <CompressText>
            {isFailedUpload && image.originalImageName && (
              <CompressFileName title={image.originalImageName}>
                {image.originalImageName}
              </CompressFileName>
            )}
            {getTranslationKey(
              isFailedUpload ? 'galleryImage.failedUpload' : 'imageIsStillCompressing'
            )}
          </CompressText>
          <CompressDeleteButton onClick={() => dispatch(deleteImage(image._id))}>
            <FaRegTrashAlt />
          </CompressDeleteButton>
        </ImageInner>
      ) : (
        <ImageInner
          data-cy="ImageIner"
          draggable={isDraggable}
          onClick={selectImageHandler}
          isDraggable={isDraggable}
          className={`${className}`}
          isSelected={isSelected}
          onDragStart={onDragStartHandler}
          onDragEnd={onDragEndHandler}
        >
          <>
            <ImageCardFavoritesIcons
              data-cy="ImageCardFavoritesIcons"
              favorite={favorite}
              imageId={image?._id}
              commentCount={commentCount}
              onCommentClick={onCommentClick}
              isTitleImage={isTitleImage || isSmallScreenTitleImage}
            />
            {(isTitleImage || isSmallScreenTitleImage) && (
              <>
                <UncontrolledTooltip autohide={false} placement="right" target={deleteTitleImageId}>
                  {getTranslationKey('galleryImage.deleteTitleImageTooltip')}
                </UncontrolledTooltip>
                <UncontrolledTooltip autohide={false} placement="right" target={setFocalPointId}>
                  {getTranslationKey('collectionSettings.focalPoint.setFocalPointTooltip')}
                </UncontrolledTooltip>
                <DeleteTitleIcon
                  data-cy="DeleteTitleTooltip"
                  size={20}
                  color="#fff"
                  id={deleteTitleImageId}
                  onClick={isTitleImage ? removeTitleImage : removeSmallScreenTitleImage}
                />
                <StarImage size={20} color="#fff" />
                <SetFocalPointIcon
                  fill="#fff"
                  id={setFocalPointId}
                  onClick={openSetFocalPointModal}
                />
              </>
            )}

            <Cover isSelected={isSelected} />
            <ImageRenderer
              image={image}
              imageSource={imageSource}
              onLoad={(e) => verifyImageDimensions(e, image)}
              onError={handleImageLoadError}
            />
          </>
        </ImageInner>
      )}

      {!noToolbar && !isStillCompressing && !isFailedUpload && (
        <ImageCardActions
          data-cy="ImageCardActions"
          image={image}
          index={index}
          hideButtons={hideActionsButtons}
          actionID={actionID}
          galleriesImages={galleriesImages}
        />
      )}
    </Wrapper>
  );
});
